import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import faqData from '../../../data/faq.json';

const FAQSection: React.FC = () => {
  const theme = useTheme();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.mode === 'dark'
          ? 'background.default'
          : 'grey.50',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              FAQ
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: '800px', mx: 'auto' }}
            >
              Häufig gestellte Fragen zu unseren Services
            </Typography>
          </motion.div>
        </Box>

        <Grid container spacing={3} sx={{ maxWidth: '1000px', mx: 'auto' }}>
          {faqData.faqs.map((faq, index) => (
            <Grid item xs={12} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    background: expandedIndex === index
                      ? `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.light}10)`
                      : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.03)'
                        : 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${expandedIndex === index ? theme.palette.primary.main : theme.palette.divider}`,
                    transition: 'all 0.3s ease-in-out',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateX(8px)',
                    },
                  }}
                  onClick={() => handleToggle(index)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: expandedIndex === index ? 600 : 500,
                          color: expandedIndex === index
                            ? theme.palette.primary.main
                            : 'text.primary',
                        }}
                      >
                        {faq.question}
                      </Typography>
                      <Box
                        sx={{
                          minWidth: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: expandedIndex === index
                            ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                            : theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.1)'
                              : 'rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        {expandedIndex === index ? (
                          <RemoveIcon
                            sx={{
                              fontSize: 18,
                              color: expandedIndex === index
                                ? 'white'
                                : 'text.secondary',
                            }}
                          />
                        ) : (
                          <AddIcon
                            sx={{
                              fontSize: 18,
                              color: 'text.secondary',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <AnimatePresence>
                      {expandedIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                              mt: 2,
                              lineHeight: 1.7,
                            }}
                          >
                            {faq.answer}
                          </Typography>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FAQSection;
